import { useState } from "react";
import type { VizEditorProps } from "./registry.js";

/**
 * Default editor for any embed kind: edit the raw fence body and its
 * attributes directly. Brutalist on purpose — it shows the actual machinery
 * (the SQL/HTML/text and the key=value attrs) rather than a bespoke form.
 *
 * Edits are live: each change splices straight into the page draft via
 * updateCode, so the preview behind the modal updates as you type.
 */
export function FenceEditor({ attrs, source, updateCode }: VizEditorProps) {
  // Local list so attr keys can be edited/renamed without losing focus.
  const [pairs, setPairs] = useState<[string, string][]>(() =>
    Object.entries(attrs),
  );

  const commit = (next: [string, string][], body = source): void => {
    const obj: Record<string, string> = {};
    for (const [k, v] of next) if (k) obj[k] = v;
    updateCode(body, obj);
  };

  const setPair = (i: number, k: string, v: string): void => {
    const next = pairs.map((p, j) => (j === i ? ([k, v] as [string, string]) : p));
    setPairs(next);
    commit(next);
  };

  const addPair = (): void => setPairs([...pairs, ["", ""]]);

  const removePair = (i: number): void => {
    const next = pairs.filter((_, j) => j !== i);
    setPairs(next);
    commit(next);
  };

  return (
    <div className="dw-fence-editor">
      <table className="dw-attr-table">
        <tbody>
          {pairs.map(([k, v], i) => (
            <tr key={i}>
              <td>
                <input
                  className="dw-attr-key"
                  value={k}
                  placeholder="key"
                  spellCheck={false}
                  onChange={(e) => setPair(i, e.target.value, v)}
                />
              </td>
              <td>
                <input
                  className="dw-attr-val"
                  value={v}
                  placeholder="value"
                  spellCheck={false}
                  onChange={(e) => setPair(i, k, e.target.value)}
                />
              </td>
              <td>
                <button className="dw-link" onClick={() => removePair(i)}>
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="dw-link" onClick={addPair}>
        + attr
      </button>
      <textarea
        className="dw-textarea dw-fence-body"
        value={source}
        spellCheck={false}
        autoFocus
        onChange={(e) => commit(pairs, e.target.value)}
      />
    </div>
  );
}
